import React from 'react';
import { Box, Typography, Alert, Chip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InvButton from '../InvButton';
import StatusChip from '../StatusChip';
import {
  WORKFLOW_STAGES,
  getWorkflowStageIndex,
  getWorkflowBanner,
  getAvailableActions,
  isPurchasableVendor,
  isVendorActive,
} from '../../utils/vendorWorkflow';
import useInvRole from '../../hooks/useInvRole';
import { filterVendorActionsByRole } from '../../utils/inventoryRoles';
import { IPD_COLORS, cardSx, hideScrollbarSx } from '../../theme';
import { invChipSx } from '../StatusChip';

function WorkflowStepper({ status }) {
  const currentIndex = getWorkflowStageIndex(status);

  return (
    <Box sx={{ position: 'relative', mx: -0.5 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        overflowX: 'auto',
        pb: 0.5,
        ...hideScrollbarSx,
      }}>
        {WORKFLOW_STAGES.map((stage, i) => {
          const isCompleted = currentIndex >= 0 && i < currentIndex;
          const isActive = i === currentIndex;
          const isLast = i === WORKFLOW_STAGES.length - 1;
          const isSideState = currentIndex === 2
            && (status === 'SUSPENDED' || status === 'BLACKLISTED')
            && i === 2;

          let circleBg = IPD_COLORS.cardBg;
          let circleBorder = IPD_COLORS.border;
          let circleColor = IPD_COLORS.primary;
          let stepLabel = stage.label;

          if (isCompleted) {
            circleBg = IPD_COLORS.primary;
            circleBorder = IPD_COLORS.primary;
            circleColor = '#fff';
          } else if (isActive) {
            circleBg = IPD_COLORS.primary;
            circleBorder = IPD_COLORS.primary;
            circleColor = '#fff';
          } else if (isSideState && status === 'SUSPENDED') {
            circleBg = IPD_COLORS.warning.text;
            circleBorder = IPD_COLORS.warning.text;
            circleColor = '#fff';
            stepLabel = 'Suspended';
          } else if (isSideState && status === 'BLACKLISTED') {
            circleBg = IPD_COLORS.error.text;
            circleBorder = IPD_COLORS.error.text;
            circleColor = '#fff';
            stepLabel = 'Blacklisted';
          }

          return (
            <Box key={stage.key} sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: 72, sm: 88 } }}>
                <Box sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${circleBorder}`,
                  background: circleBg,
                  color: circleColor,
                  fontWeight: 700,
                  fontSize: 13,
                  boxShadow: isActive ? '0 4px 12px rgba(13,35,63,0.18)' : 'none',
                  flexShrink: 0,
                }}>
                  {isCompleted ? <CheckIcon sx={{ fontSize: 18 }} /> : i + 1}
                </Box>
                <Typography sx={{
                  mt: 1,
                  fontSize: 11,
                  fontWeight: isActive ? 700 : 600,
                  color: isActive ? IPD_COLORS.primaryDark : IPD_COLORS.textSecondary,
                  textAlign: 'center',
                  lineHeight: 1.25,
                  px: 0.5,
                }}>
                  {stepLabel}
                </Typography>
              </Box>
              {!isLast && (
                <Box sx={{
                  width: { xs: 24, sm: 32 },
                  height: 2,
                  mt: '17px',
                  flexShrink: 0,
                  background: isCompleted ? IPD_COLORS.primary : IPD_COLORS.border,
                  borderRadius: 1,
                }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function PurchasableBadge({ vendor }) {
  const purchasable = isPurchasableVendor(vendor);
  return (
    <Chip
      icon={purchasable
        ? <CheckCircleOutlineIcon sx={{ fontSize: '16px !important' }} />
        : <CancelOutlinedIcon sx={{ fontSize: '16px !important' }} />}
      label={purchasable ? 'PO Ready' : 'Not PO Ready'}
      size="small"
      sx={{
        ...invChipSx({
          color: purchasable ? IPD_COLORS.success.text : IPD_COLORS.error.text,
          variant: 'soft',
        }),
        '& .MuiChip-icon': { color: 'inherit' },
      }}
    />
  );
}

export default function VendorWorkflowBar({
  vendor,
  onActionClick,
  actionLoading,
  activeAction,
  showActions = true,
  compact = false,
}) {
  // Above the early return — hooks cannot be called conditionally.
  const { can } = useInvRole();

  if (!vendor) return null;

  const status = vendor.approval_status;
  const banner = getWorkflowBanner(vendor);
  const actions = filterVendorActionsByRole(getAvailableActions(vendor), can);

  return (
    <Box sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, mb: 2.5 }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 2,
        alignItems: { xs: 'stretch', lg: 'center' },
        justifyContent: 'space-between',
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!compact && <WorkflowStepper status={status} />}
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            mt: compact ? 0 : 1.5,
          }}>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, fontWeight: 500 }}>
              Approval:
            </Typography>
            <StatusChip status={status} />
            {!isVendorActive(vendor) && <StatusChip status="INACTIVE" />}
            <PurchasableBadge vendor={vendor} />
          </Box>
        </Box>

        {showActions && actions.length > 0 && (
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            '& .MuiButton-root': { flex: { xs: '1 1 auto', sm: '0 0 auto' } },
          }}>
            {actions.map((action) => (
              <InvButton
                key={action.key}
                size="small"
                tone={action.tone || 'teal'}
                variant={action.variant || 'contained'}
                loading={actionLoading && activeAction === action.key}
                disabled={actionLoading && activeAction !== action.key}
                onClick={() => onActionClick?.(action)}
              >
                {action.label}
              </InvButton>
            ))}
          </Box>
        )}
      </Box>

      {banner && (
        <Alert severity={banner.severity} sx={{ mt: 2, py: 0.5 }}>
          {banner.text}
        </Alert>
      )}

      {vendor.rejection_reason && (
        <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
          Rejection reason: {vendor.rejection_reason}
        </Alert>
      )}
    </Box>
  );
}
